import React from 'react';
import { Tooltip } from 'antd';
import { CrownFilled, CheckCircleFilled, ReadOutlined, SolutionOutlined, StopOutlined } from '@ant-design/icons';
import Swal from 'sweetalert2';
import { calcAge, formatHeight } from '../utils/profileHelpers';

const planBadge = (plan) => {
    if (!plan || plan === 'free') return null;
    const isUltimate = plan === 'ultimate';
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '3px',
            background: isUltimate
                ? 'linear-gradient(135deg,#F59E0B,#FCD34D)'
                : 'linear-gradient(135deg,#AD1457,#D81B60)',
            borderRadius: '6px', padding: '3px 8px',
        }}>
            <CrownFilled style={{ color: isUltimate ? '#78350F' : 'white', fontSize: '10px' }} />
            <span style={{ color: isUltimate ? '#78350F' : 'white', fontSize: '0.65rem', fontWeight: 700 }}>
                {plan.toUpperCase()}
            </span>
        </span>
    );
};

const BlockedProfileCard = ({ profile, onUnblock }) => {
    const profileData = profile.blocked_profile;

    const imageUrl = profileData.profile_pictures?.[0]?.image_path
        ? `${process.env.REACT_APP_IMAGE_URL}/${profileData.profile_pictures[0].image_path}`
        : (profileData.gender?.toLowerCase() === 'female'
            ? process.env.REACT_APP_FEMALE_FALLBACK_IMAGE
            : process.env.REACT_APP_MALE_FALLBACK_IMAGE);

    const age        = calcAge(profileData.date_of_birth);
    const height     = formatHeight(profileData.physical_lifestyle?.height);
    const caste      = profileData.socio_religious_background?.caste;
    const denom      = profileData.socio_religious_background?.denomination;
    const education  = profileData.education_occupation?.highest_education;
    const occupation = profileData.education_occupation?.occupation;
    const city       = profileData.contact_information?.city;
    const state      = profileData.contact_information?.state;
    const verified   = profileData.verifications?.name_dob_status === 1;
    const location   = [city, state].filter(Boolean).join(', ');

    const handleUnblock = (e) => {
        e.stopPropagation();
        Swal.fire({
            title: 'Unblock this profile?',
            text: `${profileData.first_name} ${profileData.last_name} will be able to see your profile again.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#AD1457',
            cancelButtonColor: '#94A3B8',
            confirmButtonText: 'Yes, unblock',
        }).then((result) => {
            if (result.isConfirmed) onUnblock(profile.id);
        });
    };

    const btnBase = {
        flex: 1, border: 'none', borderRadius: '8px',
        padding: '8px 0', fontSize: '0.82rem', fontWeight: 600,
        cursor: 'pointer', display: 'flex', alignItems: 'center',
        justifyContent: 'center', gap: '5px',
        fontFamily: 'Calibri, sans-serif', transition: 'opacity 0.2s',
    };

    return (
        <div
            style={{
                background: 'white', borderRadius: '16px', overflow: 'hidden',
                border: '1px solid rgba(173,20,87,0.08)',
                transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
                fontFamily: 'Calibri, sans-serif',
                display: 'flex', flexDirection: 'column',
            }}
            onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-6px)';
                e.currentTarget.style.boxShadow = '0 12px 48px rgba(173,20,87,0.2),0 4px 12px rgba(0,0,0,0.08)';
                e.currentTarget.style.borderColor = 'rgba(173,20,87,0.2)';
            }}
            onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = 'rgba(173,20,87,0.08)';
            }}
        >
            {/* ── Photo ── */}
            <div style={{ position: 'relative', height: '220px', overflow: 'hidden', flexShrink: 0 }}>
                <img
                    alt="profile"
                    src={imageUrl}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', filter: 'grayscale(40%)' }}
                />
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(26,26,46,0.35)' }} />

                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '55%', background: 'linear-gradient(to top,rgba(26,26,46,0.78) 0%,transparent 100%)', pointerEvents: 'none' }} />

                {/* Name */}
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '10px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <span style={{ color: 'white', fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}>
                            {profileData.first_name} {profileData.last_name}
                        </span>
                        {verified && (
                            <Tooltip title="Verified Profile">
                                <CheckCircleFilled style={{ color: '#34D399', fontSize: '14px' }} />
                            </Tooltip>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: '6px', marginTop: '4px', flexWrap: 'wrap' }}>
                        {age && (
                            <span style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', borderRadius: '6px', padding: '2px 8px', color: 'rgba(255,255,255,0.9)', fontSize: '0.72rem', fontWeight: 500 }}>
                                {age} yrs
                            </span>
                        )}
                        {height && (
                            <span style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', borderRadius: '6px', padding: '2px 8px', color: 'rgba(255,255,255,0.9)', fontSize: '0.72rem', fontWeight: 500 }}>
                                {height}
                            </span>
                        )}
                    </div>
                </div>

                {/* Plan badge */}
                {profileData.subscription_plan && profileData.subscription_plan !== 'free' && (
                    <div style={{ position: 'absolute', top: '10px', right: '10px' }}>
                        {planBadge(profileData.subscription_plan)}
                    </div>
                )}

                {/* Blocked badge */}
                <div style={{
                    position: 'absolute', top: '10px', left: '10px',
                    background: 'rgba(239,68,68,0.85)', backdropFilter: 'blur(4px)',
                    borderRadius: '8px', padding: '3px 10px',
                    display: 'flex', alignItems: 'center', gap: '4px',
                }}>
                    <StopOutlined style={{ color: 'white', fontSize: '10px' }} />
                    <span style={{ color: 'white', fontSize: '0.72rem', fontWeight: 700 }}>Blocked</span>
                </div>
            </div>

            {/* ── Body ── */}
            <div style={{ padding: '12px 14px', flex: 1 }}>
                {(caste || denom) && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                        {caste && (
                            <span style={{ background: '#EDE9FE', color: '#AD1457', borderRadius: '6px', padding: '3px 10px', fontSize: '0.73rem', fontWeight: 600 }}>
                                {caste}
                            </span>
                        )}
                        {denom && (
                            <span style={{ background: '#F0FDF4', color: '#059669', borderRadius: '6px', padding: '3px 10px', fontSize: '0.73rem', fontWeight: 600 }}>
                                {denom}
                            </span>
                        )}
                    </div>
                )}

                <div style={{ marginBottom: '8px' }}>
                    {education && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                            <ReadOutlined style={{ color: '#94A3B8', fontSize: '0.82rem' }} />
                            <span style={{ color: '#475569', fontSize: '0.82rem' }}>{education}</span>
                        </div>
                    )}
                    {occupation && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                            <SolutionOutlined style={{ color: '#94A3B8', fontSize: '0.82rem' }} />
                            <span style={{ color: '#475569', fontSize: '0.82rem' }}>{occupation}</span>
                        </div>
                    )}
                    {location && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ color: '#94A3B8', fontSize: '0.82rem' }}>📍</span>
                            <span style={{ color: '#475569', fontSize: '0.82rem' }}>{location}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Action bar ── */}
            <div style={{ padding: '0 12px 12px' }}>
                <button
                    style={{ ...btnBase, width: '100%', background: 'rgba(239,68,68,0.08)', color: '#EF4444' }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                    onClick={handleUnblock}
                >
                    <StopOutlined style={{ fontSize: '11px' }} /> Unblock
                </button>
            </div>
        </div>
    );
};

export default BlockedProfileCard;
